// Apolo IDE — MemoryEditor (Sprint v6 B2).
//
// Lists user's persisted memory entries (code_user_memories). The agent
// injects these as a system message at the start of every turn — see
// apolo-api/src/lib/memoryInjector.js.

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCcw, Trash2, BookOpen } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';
import PortableMemoryPanel from './PortableMemoryPanel.jsx';

export default function MemoryEditor() {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch('/api/code-ide/memory');
    setLoading(false);
    if (!res.ok) { setError(res.error || 'Falha ao carregar memórias'); return; }
    setError(null);
    setMemories(Array.isArray(res.data?.memories) ? res.data.memories : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function add() {
    const value = draft.trim();
    if (!value) return;
    setSaving(true);
    const res = await apiFetch('/api/code-ide/memory', {
      method: 'POST',
      body: JSON.stringify({ content: value }),
    });
    setSaving(false);
    if (res.ok) { setDraft(''); load(); }
    else setError(res.error || 'Falha ao salvar memória');
  }

  async function remove(id) {
    const res = await apiFetch(`/api/code-ide/memory/${id}`, { method: 'DELETE' });
    if (res.ok) load();
  }

  return (
    <div className="flex h-full flex-col gap-3 px-3 py-3">
      {error && (
        <div role="alert" className="rounded-apolo-md border border-apolo-error/40 bg-apolo-errorBg p-2 text-xs text-apolo-error">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-apolo-textTertiary uppercase tracking-widest">
        <span className="inline-flex items-center gap-1">
          <BookOpen size={12} aria-hidden="true" /> Memória persistente
        </span>
        <button type="button" onClick={load} aria-label="Atualizar memórias" className="hover:text-apolo-textSecondary">
          <RefreshCcw size={12} />
        </button>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="memory-draft" className="sr-only">Nova memória</label>
        <textarea
          id="memory-draft"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Pedro prefere commits em inglês. Português conversacional pra narrativa."
          rows={3}
          className="rounded-apolo-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 text-sm text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:border-apolo-accent focus:outline-none"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim() || saving}
          className="inline-flex items-center gap-1 self-end rounded-apolo-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg hover:bg-apolo-accentHover disabled:opacity-50 min-h-touch"
        >
          <Plus size={12} /> {saving ? 'Salvando…' : 'Adicionar'}
        </button>
      </div>

      {/* Portable memory (POR-T04) — self-gates on Electron + memory.portable flag.
          When the user is on web OR the flag is OFF this renders nothing, so the
          legacy MemoryEditor UX is byte-identical when the feature is disabled. */}
      <PortableMemoryPanel />

      <div className="flex-1 overflow-auto">
        {loading && memories.length === 0 ? (
          <div className="text-xs text-apolo-textTertiary">Carregando…</div>
        ) : memories.length === 0 ? (
          <div className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 p-4 text-center text-xs text-apolo-textTertiary">
            Sem memórias. Adicione fatos importantes que o agente deve lembrar entre sessões.
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {memories.map((m) => (
              <li key={m.id} className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-apolo-textPrimary">{m.content}</p>
                  <button
                    type="button"
                    onClick={() => remove(m.id)}
                    aria-label={`Remover memória`}
                    className="text-apolo-textTertiary hover:text-apolo-error"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {m.created_at && (
                  <div className="mt-1 text-[10px] text-apolo-textTertiary">
                    {new Date(m.created_at).toLocaleString('pt-BR')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
